/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PublicViewConfigPage — split-pane public view builder.
 * Route: /dashboard/applications/:id/public-pages/:tableId/configure
 *
 * Left (40%): PublicViewConfigPanel with 8 accordion sections.
 * Right (60%): PublicViewPreview iframe, refreshes 1.5s after changes.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePublicViewConfig, savePublicViewConfig } from '../hooks/usePublicViewConfig';
import { usePages } from '../hooks/usePages';
import { pickRecordTemplatePages } from '../utils/recordTemplatePages';
import PublicViewConfigPanel from '../components/public-view-config/PublicViewConfigPanel';
import PublicViewPreview from '../components/public-view-config/PublicViewPreview';
import { makeChildEdgesLoader } from '../components/public-view-config/childEdgesLoader';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const PUBLIC_CLIENT = process.env.REACT_APP_PUBLIC_CLIENT_URL || '';

export default function PublicViewConfigPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: appId, tableId } = useParams();

  // ---- Load existing config ----
  const { data: savedConfig, isLoading: configLoading } = usePublicViewConfig(tableId);

  // ---- Load Page Builder pages usable as custom layouts ----
  // Only 'record_template' pages qualify: an ordinary page is reachable at
  // /<slug> and is not written against a record, so rendering one as a layout
  // would show raw {{ }} placeholders to visitors.
  const { data: pagesData } = usePages(appId);
  const recordTemplatePages = useMemo(
    () => pickRecordTemplatePages(pagesData),
    [pagesData]
  );

  // ---- Discover related tables (FK edges) for the Related Data panel ----
  // Read from stored FK metadata by the backend, so the panel offers real
  // relationships rather than asking the builder to type table/column names.
  const { data: edgesData } = useQuery({
    queryKey: ['relatedEdges', tableId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/${tableId}/related-edges/`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { edges: [], tables: [], own_fields: [] };
      return resp.json();
    },
    enabled: !!tableId,
    staleTime: 60_000,
  });
  const relatedEdges = edgesData?.edges || [];
  // Every public table in the application, plus this table's own columns, so
  // the panel can offer a join on any column rather than FK edges only.
  const relatedTables = edgesData?.tables || [];
  const ownFields = edgesData?.own_fields || [];

  // Any table in this application can be an action button's write target — it
  // does not need to be public, because writing is authenticated and the row is
  // assembled server-side from the button's own configuration.
  const { data: allTablesData } = useQuery({
    queryKey: ['tableList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200&include_fields=1`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return [];
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });
  // Three endpoints in this feature have each returned a different envelope —
  // a bare array, {results}, and {tables} — and reading the wrong one yields an
  // empty picker with no error anywhere. Accept all three rather than guess.
  const writableTables = Array.isArray(allTablesData)
    ? allTablesData
    : (allTablesData?.tables || allTablesData?.results || []);

  // Related Data can nest: a ticked table offers its own related tables (an
  // event's activities offer their route and venue). Those come from the same
  // related-edges endpoint, addressed by the child table's id, which this
  // application's table list supplies. Memoised on the list itself — a new
  // loader identity makes every nested block fetch again.
  //
  // Its own cache key, not ['relatedEdges', tableId]: that query turns a failed
  // request into an empty result, where a nested block must be told it failed.
  const loadChildEdges = useMemo(() => makeChildEdgesLoader(
    Array.isArray(allTablesData)
      ? allTablesData
      : (allTablesData?.tables || allTablesData?.results || []),
    id => queryClient.fetchQuery({
      queryKey: ['relatedEdges', 'child', String(id)],
      queryFn: async ({ signal }) => {
        const resp = await apiHelper.apiCall(
          `/arcee/application/table/${id}/related-edges/`,
          { method: 'GET', signal }
        );
        if (!resp.ok) throw new Error(`Failed to load related tables (${resp.status})`);
        return resp.json();
      },
      staleTime: 60_000,
    }),
  ), [allTablesData, queryClient]);

  // Forms an action button can open. Built in Form Builder, reused here rather
  // than a second field editor being added to action buttons.
  const { data: formsData } = useQuery({
    queryKey: ['formList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/forms/?application_id=${appId}`, { method: 'GET', signal });
      if (!resp.ok) return [];
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });
  // The endpoint returns a BARE ARRAY, like the pages one — reading .forms off
  // it silently yields an empty picker with no error anywhere.
  const forms = Array.isArray(formsData)
    ? formsData
    : (formsData?.forms || formsData?.results || []);

  // ---- Load table fields ----
  const { data: tableData } = useQuery({
    queryKey: ['tableDetails', appId, tableId],
    queryFn: async ({ signal }) => {
      if (!tableId || !appId) return null;
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/details/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ application_id: appId, table_id: Number(tableId) }),
          signal,
        }
      );
      if (!resp.ok) return null;
      return resp.json();
    },
    enabled: !!tableId && !!appId,
    staleTime: 60_000,
  });

  // ---- Local working config ----
  const [config, setConfig] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  // True once the table has been configured+saved at least once (DB record exists)
  const [isEverConfigured, setIsEverConfigured] = useState(false);

  // Preview state — debounced key to force iframe reload
  const [previewUrl, setPreviewUrl] = useState(null);
  const debounceRef = useRef(null);

  // Seed local config from saved config when it loads
  useEffect(() => {
    if (savedConfig && !config) {
      // Fill branding blanks with table metadata as defaults.
      // Backend returns view_type_key/view_type_label; map back to view_type for local state.
      const savedBranding = savedConfig.branding || {};
      setIsEverConfigured(true);
      setConfig({
        ...savedConfig,
        view_type: savedConfig.view_type_key || savedConfig.view_type || 'card',
        branding: {
          ...savedBranding,
          page_title: savedBranding.page_title || tableData?.display_name || tableData?.name || '',
          subtitle:   savedBranding.subtitle   || tableData?.description  || '',
        },
      });
    } else if (!configLoading && savedConfig === null && !config) {
      // Not yet configured — start with defaults, pre-fill branding from table metadata
      setConfig({
        view_type: 'card',
        visible_fields: [],
        field_mapping: {},
        interaction_config: {},
        branding: {
          page_title: tableData?.display_name || tableData?.name || '',
          subtitle:   tableData?.description  || '',
        },
        layout_config: {},
        custom_css: '',
        seo: {},
        is_active: true,
      });
    }
  }, [savedConfig, configLoading, config]);

  // Dirty guard on page unload
  useEffect(() => {
    function beforeUnload(e) {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [isDirty]);

  // Build preview URL — slug priority: local edit > saved table_slug > table_name > tableData.name
  function buildPreviewUrl(cfg) {
    const slug = cfg?.slug || cfg?.table_slug || cfg?.table_name || tableData?.name || '';
    const key = cfg?.public_access_key || '';
    if (!slug || !key) return null;
    return `${PUBLIC_CLIENT}/p/${slug}?preview=true&pk=${key}`;
  }

  // Merge config patch and mark dirty
  const updateConfig = useCallback((patch) => {
    setConfig(prev => {
      const merged = { ...prev, ...patch };
      // Debounce preview refresh by 1.5s
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setPreviewUrl(buildPreviewUrl(merged));
      }, 1500);
      return merged;
    });
    setIsDirty(true);
    setSaveMsg(null);
  }, [tableData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set initial preview URL when config or tableData loads (tableData provides slug fallback)
  useEffect(() => {
    if (config && !isDirty) {
      setPreviewUrl(buildPreviewUrl(config));
    }
  }, [config, isDirty, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!config) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const saved = await savePublicViewConfig(tableId, config);
      setConfig(prev => ({ ...prev, ...saved }));
      queryClient.invalidateQueries({ queryKey: ['publicViewConfig', String(tableId)] });
      setIsEverConfigured(true);
      setIsDirty(false);
      setSaveMsg({ type: 'success', text: 'Saved successfully.' });
      setPreviewUrl(buildPreviewUrl({ ...config, ...saved }));
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      setSaveMsg({ type: 'error', text: err.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  const fields = tableData?.fields || tableData?.columns || [];
  const tableName = tableData?.display_name || tableData?.name || `Table ${tableId}`;
  const publicUrl = config?.slug || tableData?.name
    ? `${PUBLIC_CLIENT}/p/${config?.slug || tableData?.name}`
    : null;

  if (configLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <span className="spinner-border" style={{ color: theme.primary }} />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh' }}>

      {/* Sticky header */}
      <div
        className="w-100 px-3 py-2 px-md-4 bg-white border-bottom d-flex align-items-center gap-3 shadow-sm"
        style={{ position: 'sticky', top: 0, zIndex: 100 }}
      >
        {/* Back */}
        <button
          className="btn btn-light btn-sm rounded-circle"
          onClick={() => {
            if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
            navigate(`/dashboard/applications/${appId}/public-pages`);
          }}
          style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <i className="bi bi-arrow-left" />
        </button>

        {/* Breadcrumb */}
        <div className="flex-grow-1 small">
          <span className="text-muted">Public Pages / </span>
          <span className="fw-semibold">{tableName}</span>
          {isDirty && (
            <span className="ms-2 text-warning fw-bold" title="Unsaved changes">●</span>
          )}
        </div>

        {/* Save message */}
        {saveMsg && (
          <span className={`small ${saveMsg.type === 'success' ? 'text-success' : 'text-danger'}`}>
            <i className={`bi ${saveMsg.type === 'success' ? 'bi-check-circle' : 'bi-x-circle'} me-1`} />
            {saveMsg.text}
          </span>
        )}

        {/* Preview link */}
        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-secondary"
          >
            <i className="bi bi-box-arrow-up-right me-1" />
            Preview
          </a>
        )}

        {/* Save */}
        <button
          className="btn btn-sm fw-semibold"
          style={{ background: theme.primary, color: '#fff', border: 'none', minWidth: 80 }}
          onClick={handleSave}
          disabled={saving || !isDirty}
        >
          {saving
            ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
            : isDirty ? 'Save' : 'Saved'
          }
        </button>
      </div>

      {/* Split pane body */}
      <div className="d-flex flex-grow-1 overflow-hidden" style={{ height: 'calc(100vh - 56px)' }}>

        {/* Left: config panel */}
        <div
          className="overflow-y-auto border-end bg-white"
          style={{ width: '40%', minWidth: 320, padding: '16px 20px' }}
        >
          {config && (
            <PublicViewConfigPanel
              tableId={tableId}
              config={config}
              updateConfig={updateConfig}
              fields={fields}
              tableDisplayName={tableData?.display_name || tableData?.name || ''}
              tableDescription={tableData?.description || ''}
              recordTemplatePages={recordTemplatePages}
              tableName={tableData?.name || ''}
              relatedEdges={relatedEdges}
              relatedTables={relatedTables}
              ownFields={ownFields}
              writableTables={writableTables}
              forms={forms}
              loadChildEdges={loadChildEdges}
            />
          )}
        </div>

        {/* Right: preview */}
        <div className="flex-grow-1 overflow-hidden" style={{ height: '100%' }}>
          <PublicViewPreview previewUrl={previewUrl} isEverConfigured={isEverConfigured} theme={theme} />
        </div>
      </div>
    </div>
  );
}
